/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Sync audiences", description = "Send audience membership to destinations such as ad platforms, CRMs, and email tools, and keep it up to date automatically.", lastUpdatedDate = new Date(1787788800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Sync audiences",
        description: "Send audience membership to destinations such as ad platforms, CRMs, and email tools, and keep it up to date automatically.",
        lastUpdatedDate: "Thu Aug 27 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How audience syncs work",
          "depth": 0,
          "id": "how-audience-syncs-work"
        }, {
          "value": "Add a sync to an audience",
          "depth": 0,
          "id": "add-a-sync-to-an-audience"
        }, {
          "value": "Control which members reach a destination",
          "depth": 0,
          "id": "control-which-members-reach-a-destination"
        }, {
          "value": "Monitor syncs",
          "depth": 0,
          "id": "monitor-syncs"
        }, {
          "value": "Use sync templates",
          "depth": 0,
          "id": "use-sync-templates"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      h2: "h2",
      ol: "ol",
      li: "li",
      img: "img",
      hr: "hr",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, Platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsxs("ul", {
                children: [_jsxs("li", {
                  children: ["A saved ", _jsx(_components.a, {
                    href: "/customer-studio/usage",
                    children: "audience"
                  })]
                }), _jsxs("li", {
                  children: ["At least one ", _jsx(_components.a, {
                    href: "/getting-started/concepts#destinations",
                    children: "destination"
                  })]
                })]
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "After you build and save an audience, you can sync it to destinations such as CRMs, ad platforms, and email tools. Hightouch keeps each destination up to date, adding and removing members as the audience changes."
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-audience-syncs-work",
        children: "How audience syncs work"
      }), "\n", _jsx(_components.p, {
        children: "A sync sends an audience's membership to a destination and keeps it current. As members enter or exit the audience, Hightouch adds or removes them in the destination on the next run."
      }), "\n", _jsx(_components.p, {
        children: "For example, if you build an audience of users who added a product to their cart without buying it, you can sync it to Meta or Google Ads to retarget them. When someone completes the purchase and leaves the audience, Hightouch removes them from the destination."
      }), "\n", _jsxs(_components.p, {
        children: ["Configuring an audience sync works like any other Hightouch sync: you ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "map fields"
        }), ", set a ", _jsx(_components.a, {
          href: "/syncs/schedule-sync-ui",
          children: "schedule"
        }), ", and add ", _jsx(_components.a, {
          href: "/syncs/alerting",
          children: "alerts"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Most syncs run on a schedule and send the full audience each run, such as hourly CRM updates. For event-driven campaigns like retargeting right after checkout, ", _jsx(_components.a, {
          href: "/real-time/overview",
          children: "realtime syncs"
        }), " send each member the moment they enter or exit the audience."]
      }), "\n", _jsx(_components.h2, {
        id: "add-a-sync-to-an-audience",
        children: "Add a sync to an audience"
      }), "\n", _jsx(_components.p, {
        children: "You add a sync from a saved audience:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open a saved audience and select ", _jsx(_components.strong, {
            children: "Add sync"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select a destination."
        }), "\n", _jsx(_components.li, {
          children: "Configure the sync and save."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/add-sync-button.png",
          alt: "Add a sync to an audience"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For field mapping, scheduling, and alerts, see the ", _jsx(_components.a, {
          href: "/syncs/overview",
          children: "syncs overview"
        }), ". For destination-specific settings, see that ", _jsx(_components.a, {
          href: "/destinations/overview",
          children: "destination's docs"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "control-which-members-reach-a-destination",
        children: "Control which members reach a destination"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, a sync sends the full audience to the destination. To filter which records reach a specific destination for consent, privacy, or compliance, apply a ", _jsx(_components.a, {
          href: "/customer-studio/destination-rules",
          children: "destination rule"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Destination rules run at sync time and don't change audience membership. For example, a rule can exclude users who haven't opted in to email before any records sync to your ESP."
      }), "\n", _jsx(_components.h2, {
        id: "monitor-syncs",
        children: "Monitor syncs"
      }), "\n", _jsxs(_components.p, {
        children: ["Monitor and manage an audience's syncs from its ", _jsx(_components.strong, {
          children: "Syncs"
        }), " tab. Each row shows a sync's status, last run, and how many members matched in the destination, and lets you run, pause, or edit the sync."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/sync/syncs-tab.png",
          alt: "Syncs tab"
        })
      }), "\n", _jsx(_components.h2, {
        id: "use-sync-templates",
        children: "Use sync templates"
      }), "\n", _jsxs(_components.p, {
        children: ["If your workspace has ", _jsx(_components.a, {
          href: "/customer-studio/templates#sync-templates",
          children: "sync templates"
        }), ", you can apply one when you create a sync to standardize the configuration across destinations or teams."]
      }), "\n", _jsx(_components.p, {
        children: "A template can pre-fill the destination, identifier field, field mappings, list name, and removal behavior."
      }), "\n", _jsxs(_components.p, {
        children: ["Learn how to ", _jsx(_components.a, {
          href: "/customer-studio/templates#create-a-sync-template",
          children: "create and apply a sync template"
        }), "."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/usage",
            children: "Build an audience"
          }), " to create the audience you want to sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/real-time/overview",
            children: "Realtime audiences"
          }), " to keep destinations updated as customers act, instead of on a schedule."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/syncs/overview",
            children: "Syncs overview"
          }), " for the scheduling, monitoring, and configuration options shared across all Hightouch syncs."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
